import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Plug, ChevronUp, ChevronDown, KeyRound, Search, Filter as FilterIcon, Settings2,
  CheckCircle2, AlertTriangle, Check, RefreshCw, AtSign, User as UserIcon, Calendar as CalendarIcon,
  Zap, Clock, Shield, ExternalLink, Lightbulb, Sparkles, GitBranch, Play, Save, BookOpen,
  Globe, XOctagon, Bell, Database, ShieldCheck, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/feature/progress";
import { SocialDot } from "@/components/feature/social-dot";
import { api, REDES_DISPONIBLES, type ProjectOut } from "@/lib/api";
import { useProfiles } from "@/hooks/use-profiles";
import { usePatchProject } from "@/hooks/use-projects-mutations";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/_auth/monitor/$projectId_/setup")({
  component: ProjectSetupPage,
});

// ============================================================
// Network metadata
// ============================================================

type NetworkMeta = {
  code: string;
  label: string;
  color: string;
  bg: string;
  api: string;
  rateInfo: string;
  helpUrl: string;
  fields: string[];
  examples: string[];
  scopes: string[];
  extras: { id: string; label: string; def: boolean }[];
};

const NETWORKS: Record<string, NetworkMeta> = {
  x: {
    code: "x",
    label: "X (Twitter)",
    color: "#0EA5E9",
    bg: "from-sky-50 to-white dark:from-sky-900/20 dark:to-ink-900",
    api: "X API v2 · Bearer + OAuth 2.0 PKCE",
    rateInfo: "Hasta 500.000 tweets / mes · ventana 7 días",
    helpUrl: "https://developer.x.com",
    fields: ["from:", "to:", "#hashtag", "@user", "lang:", "place:", "has:media", "has:links", "is:retweet", "-is:reply"],
    examples: [
      `("mi marca" OR @cuenta) AND lang:es -is:retweet`,
      `from:cuenta OR to:cuenta`,
      `#hashtag has:media place:PE`,
    ],
    scopes: ["tweet.read", "users.read", "offline.access"],
    extras: [
      { id: "rts", label: "Incluir retweets", def: false },
      { id: "replies", label: "Incluir respuestas", def: true },
      { id: "quotes", label: "Incluir quote-tweets", def: true },
      { id: "verified", label: "Solo cuentas verificadas", def: false },
    ],
  },
  fb: {
    code: "fb",
    label: "Facebook",
    color: "#1877F2",
    bg: "from-blue-50 to-white dark:from-blue-900/20 dark:to-ink-900",
    api: "Meta Graph API · App + Page tokens",
    rateInfo: "Páginas públicas + grupos donde eres admin",
    helpUrl: "https://developers.facebook.com",
    fields: ["page:", "group:", "#hashtag", "author:", "lang:", "country:"],
    examples: [
      `(page:micuenta OR "Mi Marca") AND (envío OR pedido)`,
      `group:"Comunidad Lima" #miHashtag`,
    ],
    scopes: ["pages_read_engagement", "pages_manage_metadata", "pages_show_list", "public_profile", "read_insights"],
    extras: [
      { id: "pages", label: "Páginas públicas", def: true },
      { id: "groups", label: "Grupos (donde eres admin)", def: false },
      { id: "comments", label: "Incluir comentarios", def: true },
      { id: "reactions", label: "Recolectar reacciones", def: true },
    ],
  },
  ig: {
    code: "ig",
    label: "Instagram",
    color: "#E1306C",
    bg: "from-pink-50 to-white dark:from-pink-900/20 dark:to-ink-900",
    api: "Instagram Graph API · cuenta Business / Creator",
    rateInfo: "Hasta 30 hashtags / 7 días por cuenta",
    helpUrl: "https://developers.facebook.com/docs/instagram-api",
    fields: ["#hashtag", "@user", "mention:", "has:reels", "has:carousel", "lang:"],
    examples: [
      `(#mimarca OR #mihashtag) AND mention:@micuenta`,
      `#origen has:carousel`,
    ],
    scopes: ["instagram_basic", "instagram_manage_insights", "instagram_manage_comments", "pages_show_list"],
    extras: [
      { id: "hashtag", label: "Hashtag tracking", def: true },
      { id: "mentions", label: "Menciones a tu cuenta", def: true },
      { id: "tagged", label: "Etiquetas en fotos", def: true },
      { id: "stories", label: "Stories (donde eres mencionado)", def: false },
      { id: "reels", label: "Incluir Reels", def: true },
    ],
  },
  tt: {
    code: "tt",
    label: "TikTok",
    color: "#111827",
    bg: "from-ink-100 to-white dark:from-ink-800 dark:to-ink-900",
    api: "TikTok Research API · acceso aprobado",
    rateInfo: "Acceso institucional · sin SLA",
    helpUrl: "https://developers.tiktok.com",
    fields: ["#hashtag", "@user", "sound:", "duet_with:", "lang:", "region:"],
    examples: [
      `(#mimarca OR @micuenta) AND region:PE`,
      `sound:"audio original"`,
    ],
    scopes: ["user.info.basic", "video.list", "research.data.basic"],
    extras: [
      { id: "duets", label: "Duets y stitches", def: true },
      { id: "sounds", label: "Tracking por sonido", def: true },
      { id: "effects", label: "Tracking por efectos", def: false },
      { id: "min_views", label: "Mín. 1.000 views", def: true },
    ],
  },
  li: {
    code: "li",
    label: "LinkedIn",
    color: "#0A66C2",
    bg: "from-blue-50 to-white dark:from-blue-900/20 dark:to-ink-900",
    api: "LinkedIn Marketing API",
    rateInfo: "Requiere aprobación Partner Program",
    helpUrl: "https://learn.microsoft.com/en-us/linkedin/",
    fields: ["company:", "author:", "#hashtag", "lang:"],
    examples: [`(company:mimarca OR "Mi Marca") AND lang:es`],
    scopes: ["r_organization_social", "rw_organization_admin"],
    extras: [
      { id: "company_posts", label: "Posts de empresa", def: true },
      { id: "employee_posts", label: "Posts de empleados", def: false },
      { id: "comments", label: "Incluir comentarios", def: true },
    ],
  },
  yt: {
    code: "yt",
    label: "YouTube",
    color: "#FF0000",
    bg: "from-red-50 to-white dark:from-red-900/20 dark:to-ink-900",
    api: "YouTube Data API v3",
    rateInfo: "10.000 units/día gratis",
    helpUrl: "https://developers.google.com/youtube/v3",
    fields: ["channel:", "video:", "#tag", "lang:", "duration:"],
    examples: [`"mi marca" lang:es`, `channel:UCxxxx`],
    scopes: ["youtube.readonly"],
    extras: [
      { id: "comments", label: "Incluir comentarios de videos", def: true },
      { id: "shorts", label: "Incluir Shorts", def: true },
      { id: "captions", label: "Analizar transcripciones", def: false },
    ],
  },
  reddit: {
    code: "reddit",
    label: "Reddit",
    color: "#FF4500",
    bg: "from-orange-50 to-white dark:from-orange-900/20 dark:to-ink-900",
    api: "Reddit API · OAuth",
    rateInfo: "100 req/min autenticado",
    helpUrl: "https://www.reddit.com/dev/api/",
    fields: ["subreddit:", "author:", "flair:", "self:", "nsfw:"],
    examples: [`"mi marca" subreddit:peru`, `author:usuario flair:review`],
    scopes: ["read", "history", "identity"],
    extras: [
      { id: "posts", label: "Posts", def: true },
      { id: "comments", label: "Comentarios", def: true },
      { id: "nsfw_exclude", label: "Excluir NSFW", def: true },
    ],
  },
  news: {
    code: "news",
    label: "Noticias (RSS)",
    color: "#6B7280",
    bg: "from-ink-50 to-white dark:from-ink-800 dark:to-ink-900",
    api: "feedparser · Google News RSS",
    rateInfo: "Sin auth · ilimitado",
    helpUrl: "https://news.google.com",
    fields: ["site:", "lang:", "country:"],
    examples: [`"mi marca" lang:es country:PE`, `site:elcomercio.pe`],
    scopes: [],
    extras: [
      { id: "google_news", label: "Google News", def: true },
      { id: "rss_custom", label: "RSS personalizados", def: false },
    ],
  },
};

const NETWORK_ORDER = ["x", "fb", "ig", "tt", "li", "yt", "reddit", "news"] as const;

// ============================================================
// Main page
// ============================================================

function ProjectSetupPage() {
  const { projectId } = Route.useParams();
  const id = Number(projectId);
  const navigate = useNavigate();

  const projectQ = useQuery({
    queryKey: ["projects", id],
    queryFn: () => api.getProject(id),
  });
  const profilesQ = useProfiles();
  const oauthProvidersQ = useQuery({
    queryKey: ["oauth", "providers"],
    queryFn: () => api.oauth.listProviders(),
    staleTime: 5 * 60_000,
  });
  const patch = usePatchProject(id);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [queries, setQueries] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!projectQ.data) return;
    const initialQueries: Record<string, string> = { ...projectQ.data.network_queries };
    const initialEnabled: Record<string, boolean> = {};
    for (const code of NETWORK_ORDER) {
      initialEnabled[code] = projectQ.data.networks.includes(code);
      if (initialQueries[code] === undefined) initialQueries[code] = "";
    }
    setQueries(initialQueries);
    setEnabled(initialEnabled);
    // expand first enabled by default
    const firstEnabled = NETWORK_ORDER.find((c) => initialEnabled[c]);
    if (firstEnabled) setExpanded({ [firstEnabled]: true });
  }, [projectQ.data]);

  function toggleExpand(n: string) {
    setExpanded((e) => ({ ...e, [n]: !e[n] }));
  }
  function expandAll() {
    setExpanded(Object.fromEntries(NETWORK_ORDER.map((n) => [n, true])));
  }
  function collapseAll() {
    setExpanded({});
  }

  async function saveAll() {
    const networks = NETWORK_ORDER.filter((n) => enabled[n]);
    const network_queries: Record<string, string> = {};
    for (const code of networks) {
      const q = (queries[code] || "").trim();
      if (q) network_queries[code] = q;
    }
    await patch.mutateAsync({ networks: [...networks], network_queries });
  }

  if (projectQ.isLoading) {
    return (
      <div className="px-6 py-6 max-w-[1180px] mx-auto space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!projectQ.data) {
    return (
      <div className="px-6 py-6 max-w-[1180px] mx-auto">
        <p className="text-sm text-bad">Proyecto no encontrado.</p>
      </div>
    );
  }

  const project = projectQ.data;
  const totalConfigurable = NETWORK_ORDER.length;
  const configured = NETWORK_ORDER.filter((n) => enabled[n]).length;
  const pct = Math.round((configured / totalConfigurable) * 100);

  return (
    <div className="px-6 py-6 max-w-[1180px] mx-auto">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <Link
            to="/monitor/$projectId"
            params={{ projectId: String(id) }}
            className="text-xs text-ink-500 hover:text-brand-600 flex items-center gap-1 mb-1"
          >
            <ArrowLeft size={12} /> Volver al proyecto
          </Link>
          <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Configuración de fuentes</h1>
          <p className="text-sm text-ink-500 mt-1">
            {project.name} · gestiona cada red social de forma independiente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Colapsar todas
          </Button>
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expandir todas
          </Button>
          <Button
            size="sm"
            onClick={async () => {
              await saveAll();
              navigate({ to: "/monitor/$projectId", params: { projectId: String(id) } });
            }}
            disabled={patch.isPending}
          >
            <Save size={14} /> {patch.isPending ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </header>

      {/* Overall progress */}
      <div className="bg-white dark:bg-ink-900 ring-soft rounded-xl2 p-4 mb-5 shadow-card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300 flex items-center justify-center shrink-0">
            <Plug size={18} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-ink-800 dark:text-ink-100">
                Setup del proyecto · {configured} de {totalConfigurable} redes activadas
              </span>
              <span className="text-xs text-ink-500">{pct}% completado</span>
            </div>
            <Progress value={pct} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {NETWORK_ORDER.map((n) => {
              const meta = NETWORKS[n];
              const isOn = enabled[n];
              const profilesForNetwork = (profilesQ.data ?? []).filter((p) => p.network === n);
              const hasConn = profilesForNetwork.some((p) => p.status === "connected");
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    setExpanded((e) => ({ ...e, [n]: true }));
                    setTimeout(
                      () =>
                        document
                          .getElementById(`net-card-${n}`)
                          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                      50,
                    );
                  }}
                  title={meta.label}
                  className="relative w-9 h-9 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 hover:ring-brand-500 flex items-center justify-center transition-colors"
                >
                  <SocialDot network={n} size={20} />
                  <span
                    className={cn(
                      "absolute -bottom-1 -right-1 w-3 h-3 rounded-full ring-2 ring-white dark:ring-ink-900",
                      isOn && hasConn ? "bg-good" : isOn ? "bg-warn" : "bg-ink-300 dark:bg-ink-700",
                    )}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Network cards */}
      <div className="space-y-4">
        {NETWORK_ORDER.map((n) => (
          <NetworkCard
            key={n}
            network={n}
            project={project}
            expanded={!!expanded[n]}
            onToggleExpand={() => toggleExpand(n)}
            enabled={!!enabled[n]}
            onToggleEnabled={(v) => setEnabled((e) => ({ ...e, [n]: v }))}
            query={queries[n] ?? ""}
            onQueryChange={(q) => setQueries((p) => ({ ...p, [n]: q }))}
            profiles={(profilesQ.data ?? []).filter((p) => p.network === n)}
            providerConfigured={
              (oauthProvidersQ.data ?? []).find((p) => p.network === n)?.configured ?? false
            }
          />
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-3 py-4 px-5 rounded-xl ring-soft bg-white dark:bg-ink-900 shadow-card">
        <div className="flex items-center gap-3">
          <ShieldCheck size={18} className="text-good" />
          <div>
            <p className="text-sm font-medium text-ink-900 dark:text-white">
              Tus cambios se aplicarán a la próxima recolección
            </p>
            <p className="text-xs text-ink-500">La sincronización tarda entre 1 y 5 minutos según la red.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/monitor/$projectId" params={{ projectId: String(id) }}>
            <Button variant="outline" size="sm">
              Descartar
            </Button>
          </Link>
          <Button size="sm" onClick={saveAll} disabled={patch.isPending}>
            <Save size={14} /> Guardar cambios
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// NetworkCard
// ============================================================

type Profile = {
  id: number;
  network: string;
  kind: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  status: string;
};

type Section = "auth" | "query" | "filters" | "options";

function NetworkCard({
  network,
  project,
  expanded,
  onToggleExpand,
  enabled,
  onToggleEnabled,
  query,
  onQueryChange,
  profiles,
  providerConfigured,
}: {
  network: string;
  project: ProjectOut;
  expanded: boolean;
  onToggleExpand: () => void;
  enabled: boolean;
  onToggleEnabled: (v: boolean) => void;
  query: string;
  onQueryChange: (q: string) => void;
  profiles: Profile[];
  providerConfigured: boolean;
}) {
  const meta = NETWORKS[network];
  const [section, setSection] = useState<Section>("auth");
  const [tested, setTested] = useState(false);

  const implementado = REDES_DISPONIBLES.find((r) => r.code === network)?.implementado ?? false;
  const connected = profiles.some((p) => p.status === "connected");
  const primaryProfile = profiles.find((p) => p.status === "connected") ?? profiles[0];

  const validation = useMemo(() => {
    if (!query.trim()) return { ok: false as const, error: "La query está vacía" };
    let depth = 0;
    for (const ch of query) {
      if (ch === "(") depth++;
      if (ch === ")") depth--;
      if (depth < 0) return { ok: false as const, error: "Paréntesis desbalanceados" };
    }
    if (depth !== 0) return { ok: false as const, error: "Paréntesis desbalanceados" };
    const dq = (query.match(/"/g) || []).length;
    if (dq % 2 !== 0) return { ok: false as const, error: "Comillas sin cerrar" };
    return { ok: true as const, count: Math.round(180 + query.length * 3) };
  }, [query]);

  useEffect(() => {
    setTested(false);
  }, [query]);

  function insertToken(t: string) {
    const sp = query && !query.endsWith(" ") ? " " : "";
    onQueryChange(query + sp + t);
  }

  async function startOAuth() {
    if (!providerConfigured) {
      toast.error(`Provider ${meta.label} no configurado en el backend.`);
      return;
    }
    try {
      const r = await api.oauth.start(network);
      window.location.href = r.authorize_url;
    } catch {
      toast.error("No se pudo iniciar OAuth");
    }
  }

  return (
    <div
      id={`net-card-${network}`}
      className="bg-white dark:bg-ink-900 rounded-xl2 ring-soft shadow-card overflow-hidden"
    >
      {/* HEADER */}
      <div className={cn("relative bg-gradient-to-r", meta.bg)}>
        <div style={{ background: meta.color }} className="absolute left-0 top-0 bottom-0 w-1" />
        <div className="flex items-center gap-4 px-5 py-4 flex-wrap">
          <div
            style={{ background: meta.color, boxShadow: `0 6px 18px -6px ${meta.color}80` }}
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
          >
            <SocialDot network={network} size={28} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-ink-900 dark:text-white">{meta.label}</h2>
              {!enabled ? (
                <StatusChip tone="neutral">Pausado</StatusChip>
              ) : connected ? (
                <StatusChip tone="good" icon={CheckCircle2}>
                  Conectado
                </StatusChip>
              ) : (
                <StatusChip tone="warn" icon={AlertTriangle}>
                  Falta autenticar
                </StatusChip>
              )}
              {connected && enabled && <StatusChip tone="brand">Recolectando</StatusChip>}
              {!implementado && (
                <StatusChip tone="neutral" icon={AlertTriangle}>
                  Worker pendiente
                </StatusChip>
              )}
            </div>
            <p className="text-xs text-ink-500 mt-0.5">
              {meta.api} · {meta.rateInfo}
            </p>
          </div>

          {connected && enabled && (
            <div className="hidden md:flex items-center gap-5 px-4 border-l border-r border-ink-200/60 dark:border-ink-700/60 h-12">
              <MiniMet label="Perfiles" value={String(profiles.length)} />
              <MiniMet label="Última sync" value={profiles[0]?.status === "connected" ? "activo" : "—"} />
            </div>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" title={enabled ? "Pausar" : "Activar"}>
              <span className="text-xs font-medium text-ink-500 hidden sm:inline">
                {enabled ? "Activo" : "Pausado"}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleEnabled(!enabled);
                }}
                className={cn(
                  "h-[22px] w-10 rounded-full relative transition-colors",
                  enabled ? "bg-brand-600" : "bg-ink-300 dark:bg-ink-700",
                )}
                aria-pressed={enabled}
                aria-label={enabled ? "Desactivar red" : "Activar red"}
              >
                <span
                  className={cn(
                    "absolute top-[3px] h-4 w-4 rounded-full bg-white shadow transition-all",
                    enabled ? "left-[21px]" : "left-[3px]",
                  )}
                />
              </button>
            </label>
            <button
              type="button"
              onClick={onToggleExpand}
              className="w-9 h-9 rounded-lg hover:bg-white/60 dark:hover:bg-ink-800 text-ink-600 dark:text-ink-300 flex items-center justify-center"
              aria-label={expanded ? "Colapsar" : "Expandir"}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>

        {/* Tabs */}
        {expanded && (
          <div className="flex items-center gap-1 px-5 -mb-px border-t border-ink-200/60 dark:border-ink-700/60 bg-white/40 dark:bg-ink-900/40 overflow-x-auto">
            {(
              [
                { id: "auth", label: "Autenticación", Icon: KeyRound },
                { id: "query", label: "Query de búsqueda", Icon: Search },
                { id: "filters", label: "Filtros", Icon: FilterIcon },
                { id: "options", label: `Opciones de ${meta.label}`, Icon: Settings2 },
              ] as const
            ).map((t) => {
              const TIcon = t.Icon;
              const active = section === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSection(t.id)}
                  className={cn(
                    "px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                    active
                      ? "border-brand-600 text-brand-700 dark:text-brand-300"
                      : "border-transparent text-ink-500 hover:text-ink-800 dark:hover:text-ink-200",
                  )}
                >
                  <TIcon size={13} /> {t.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* BODY */}
      {expanded && (
        <div className="p-5 space-y-5">
          {section === "auth" && (
            <AuthSection
              meta={meta}
              connected={connected}
              primaryProfile={primaryProfile}
              profiles={profiles}
              onConnect={startOAuth}
              providerConfigured={providerConfigured}
            />
          )}
          {section === "query" && (
            <QuerySection
              meta={meta}
              network={network}
              query={query}
              onQueryChange={onQueryChange}
              validation={validation}
              insertToken={insertToken}
              tested={tested}
              setTested={setTested}
              connected={connected}
            />
          )}
          {section === "filters" && <FiltersSection />}
          {section === "options" && <OptionsSection meta={meta} />}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Subcomponents
// ============================================================

function MiniMet({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-500">{label}</div>
      <div className="text-sm font-bold text-ink-900 dark:text-white">{value}</div>
    </div>
  );
}

function StatusChip({
  tone,
  icon: IconCmp,
  children,
}: {
  tone: "good" | "warn" | "neutral" | "brand";
  icon?: typeof CheckCircle2;
  children: React.ReactNode;
}) {
  const tones: Record<typeof tone, string> = {
    good: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
    warn: "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
    neutral: "bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200",
    brand: "bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium",
        tones[tone],
      )}
    >
      {IconCmp && <IconCmp size={11} />}
      {children}
    </span>
  );
}

// ----- Auth -----

function AuthSection({
  meta,
  connected,
  primaryProfile,
  profiles,
  onConnect,
  providerConfigured,
}: {
  meta: NetworkMeta;
  connected: boolean;
  primaryProfile: Profile | undefined;
  profiles: Profile[];
  onConnect: () => void;
  providerConfigured: boolean;
}) {
  if (connected && primaryProfile) {
    return (
      <div>
        <div className="flex items-center gap-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800">
          <div className="w-10 h-10 rounded-full bg-good text-white flex items-center justify-center shrink-0">
            <Check size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              Cuenta conectada exitosamente
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-300 truncate">
              {primaryProfile.handle} · autenticación OAuth válida
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onConnect}>
              <RefreshCw size={14} /> Reautenticar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          <ConnDetail icon={AtSign} label="Cuenta autenticada" value={primaryProfile.handle} />
          <ConnDetail icon={UserIcon} label="Tipo" value={primaryProfile.kind === "analytic" ? "Analítico" : "CRM"} />
          <ConnDetail icon={KeyRound} label="Permisos" value={`${meta.scopes.length} scopes`} />
          <ConnDetail
            icon={CalendarIcon}
            label="Estado"
            value={primaryProfile.status === "connected" ? "Activo" : primaryProfile.status}
          />
          <ConnDetail icon={Zap} label="Rate limit" value={meta.rateInfo.split("·")[0]?.trim() || "—"} />
          <ConnDetail icon={Clock} label="Perfiles vinculados" value={String(profiles.length)} />
        </div>

        {meta.scopes.length > 0 && (
          <details className="mt-5 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700">
            <summary className="px-4 py-3 flex items-center justify-between text-sm font-medium cursor-pointer list-none">
              <span className="flex items-center gap-2">
                <Shield size={14} /> Permisos detallados
              </span>
              <ChevronDown size={14} className="text-ink-400" />
            </summary>
            <div className="px-4 pb-4 space-y-2">
              {meta.scopes.map((s) => (
                <div key={s} className="flex items-center gap-2 text-xs">
                  <Check size={12} className="text-good" />
                  <code className="font-mono text-ink-700 dark:text-ink-200">{s}</code>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl ring-1 ring-dashed ring-ink-300 dark:ring-ink-700 p-8 text-center">
      <div
        className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
        style={{ background: meta.color + "15" }}
      >
        <SocialDot network={meta.code} size={28} />
      </div>
      <h3 className="text-lg font-semibold text-ink-900 dark:text-white">Conecta tu cuenta de {meta.label}</h3>
      <p className="text-sm text-ink-500 mt-1 max-w-md mx-auto">
        Te redirigiremos a {meta.label} para autorizar el acceso. Solo solicitamos los permisos mínimos
        necesarios.
      </p>
      <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
        <Button
          size="lg"
          onClick={onConnect}
          disabled={!providerConfigured}
          style={providerConfigured ? { background: meta.color } : undefined}
          className="text-white"
        >
          <Link2 size={16} /> Continuar con {meta.label}
        </Button>
        <a href={meta.helpUrl} target="_blank" rel="noreferrer">
          <Button variant="outline" size="lg">
            <ExternalLink size={16} /> Ver documentación
          </Button>
        </a>
      </div>
      {!providerConfigured && (
        <p className="text-[11px] text-warn mt-3">
          Provider no configurado en el backend. Pide al superadmin que configure las credenciales OAuth.
        </p>
      )}
      <p className="text-[11px] text-ink-400 mt-5 flex items-center justify-center gap-1.5">
        <ShieldCheck size={12} /> Conexión segura · OAuth 2.0 · puedes revocar en cualquier momento
      </p>
    </div>
  );
}

function ConnDetail({
  icon: IconCmp,
  label,
  value,
}: {
  icon: typeof AtSign;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg ring-1 ring-ink-100 dark:ring-ink-800 p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink-500 flex items-center gap-1.5">
        <IconCmp size={11} /> {label}
      </div>
      <div className="text-sm font-medium mt-1 text-ink-900 dark:text-white truncate">{value}</div>
    </div>
  );
}

// ----- Query -----

function QuerySection({
  meta,
  network,
  query,
  onQueryChange,
  validation,
  insertToken,
  tested,
  setTested,
  connected,
}: {
  meta: NetworkMeta;
  network: string;
  query: string;
  onQueryChange: (q: string) => void;
  validation: { ok: true; count: number } | { ok: false; error: string };
  insertToken: (t: string) => void;
  tested: boolean;
  setTested: (v: boolean) => void;
  connected: boolean;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-4">
        <div className="space-y-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">
              Operadores booleanos
            </p>
            <div className="flex flex-wrap gap-1.5">
              {["AND", "OR", "NOT", "( )", '"…"'].map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => insertToken(op === "( )" ? "()" : op === '"…"' ? '""' : op)}
                  className="px-2.5 py-1 rounded-md font-mono text-xs bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors"
                >
                  {op}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">
              Campos específicos de {meta.label}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {meta.fields.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => insertToken(f)}
                  className="px-2.5 py-1 rounded-md font-mono text-xs bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors"
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5 block">
            Query booleana
          </label>
          <div
            className={cn(
              "rounded-lg ring-1 bg-white dark:bg-ink-900 transition-shadow",
              validation.ok
                ? "ring-ink-200 dark:ring-ink-700 focus-within:ring-brand-500 focus-within:ring-2"
                : "ring-bad/60",
            )}
          >
            <textarea
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              rows={5}
              spellCheck={false}
              placeholder={`Ejemplo: ${meta.examples[0]}`}
              className="w-full px-3.5 py-3 text-sm bg-transparent outline-none resize-none font-mono leading-relaxed text-ink-900 dark:text-white"
              aria-label={`Query booleana para ${meta.label}`}
            />
            <div className="flex items-center justify-between px-3.5 py-2 border-t border-ink-100 dark:border-ink-800 text-[11px] flex-wrap gap-2">
              <div className="flex items-center gap-3">
                {validation.ok ? (
                  <>
                    <span className="flex items-center gap-1.5 text-good font-medium">
                      <CheckCircle2 size={12} /> Sintaxis válida
                    </span>
                    <span className="text-ink-500">≈ {validation.count} resultados / día</span>
                  </>
                ) : (
                  <span className="flex items-center gap-1.5 text-bad font-medium">
                    <AlertTriangle size={12} /> {validation.error}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-ink-400">{query.length} chars</span>
                <button
                  type="button"
                  onClick={() => onQueryChange("")}
                  className="text-ink-500 hover:text-bad"
                >
                  Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>

        {validation.ok && <QueryTree query={query} />}

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => setTested(true)}
            disabled={!validation.ok || !connected}
            title={!connected ? "Conecta una cuenta para probar" : undefined}
          >
            <Play size={14} /> Probar query
          </Button>
          <Button variant="outline" size="sm">
            <Save size={14} /> Guardar como plantilla
          </Button>
          <Button variant="ghost" size="sm">
            <BookOpen size={14} /> Ver guía de sintaxis
          </Button>
        </div>

        {tested && (
          <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-ink-100 dark:border-ink-800 flex items-center justify-between bg-ink-50/60 dark:bg-ink-950/40">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-ink-800 dark:text-ink-100">
                  Vista previa · últimas 24h
                </span>
                <StatusChip tone="good" icon={Check}>
                  Match
                </StatusChip>
              </div>
              <span className="text-xs text-ink-500">Resultados de muestra del worker</span>
            </div>
            <div className="px-4 py-6 text-center text-xs text-ink-500">
              La prueba se ejecutará contra el worker. Esto puede demorar entre 30 y 90 segundos.
            </div>
          </div>
        )}
      </div>

      <aside className="space-y-4">
        <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4">
          <h4 className="text-sm font-semibold text-ink-900 dark:text-white flex items-center gap-1.5">
            <Lightbulb size={14} className="text-warn" /> Ejemplos de query
          </h4>
          <p className="text-[11px] text-ink-500 mt-1 mb-3">Pulsa para usar como base</p>
          <div className="space-y-2">
            {meta.examples.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onQueryChange(ex)}
                className="w-full text-left p-2.5 rounded-md bg-ink-50 dark:bg-ink-800 hover:bg-brand-50 dark:hover:bg-brand-900/30 hover:ring-1 hover:ring-brand-200 transition-colors"
              >
                <code className="font-mono text-[11px] text-ink-700 dark:text-ink-200 break-words">{ex}</code>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl ring-1 ring-brand-100 dark:ring-brand-900/40 bg-brand-50/60 dark:bg-brand-900/20 p-4">
          <h4 className="text-sm font-semibold text-brand-800 dark:text-brand-200 flex items-center gap-1.5">
            <Sparkles size={14} /> IA sugiere
          </h4>
          <p className="text-xs text-brand-700 dark:text-brand-300 mt-1.5 leading-relaxed">
            Agrega variantes idiomáticas y excluye palabras irrelevantes para reducir ruido.
          </p>
        </div>

        <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4">
          <h4 className="text-sm font-semibold text-ink-900 dark:text-white">Sintaxis rápida</h4>
          <dl className="mt-3 space-y-2 text-xs">
            <SyntaxRow t='"frase exacta"' d="Coincidencia exacta" />
            <SyntaxRow t="AND / OR" d="Combinación lógica" />
            <SyntaxRow t="NOT / -term" d="Excluir términos" />
            <SyntaxRow t="( )" d="Agrupar condiciones" />
            <SyntaxRow t="campo:valor" d="Filtros específicos" />
          </dl>
          <a
            href={meta.helpUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700"
          >
            Documentación de {meta.label} <ExternalLink size={11} />
          </a>
        </div>
      </aside>
    </div>
  );
}

function SyntaxRow({ t, d }: { t: string; d: string }) {
  return (
    <div className="flex items-start gap-2">
      <code className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200 shrink-0">
        {t}
      </code>
      <span className="text-ink-500 flex-1">{d}</span>
    </div>
  );
}

// ----- Query Tree -----

type QToken =
  | { t: "paren"; v: string }
  | { t: "phrase"; v: string }
  | { t: "op"; v: string }
  | { t: "neg"; v: string }
  | { t: "tag"; v: string }
  | { t: "user"; v: string }
  | { t: "field"; v: string }
  | { t: "term"; v: string };

function tokenize(query: string): QToken[] {
  const out: QToken[] = [];
  let i = 0;
  while (i < query.length) {
    const c = query[i];
    if (c === " ") {
      i++;
      continue;
    }
    if (c === "(" || c === ")") {
      out.push({ t: "paren", v: c });
      i++;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      while (j < query.length && query[j] !== '"') j++;
      out.push({ t: "phrase", v: query.slice(i, j + 1) });
      i = j + 1;
      continue;
    }
    let j = i;
    while (j < query.length && query[j] !== " " && query[j] !== "(" && query[j] !== ")") j++;
    const w = query.slice(i, j);
    if (/^(AND|OR|NOT)$/.test(w)) out.push({ t: "op", v: w });
    else if (w.startsWith("-")) out.push({ t: "neg", v: w });
    else if (w.startsWith("#")) out.push({ t: "tag", v: w });
    else if (w.startsWith("@")) out.push({ t: "user", v: w });
    else if (w.includes(":")) out.push({ t: "field", v: w });
    else out.push({ t: "term", v: w });
    i = j;
  }
  return out;
}

function QueryTree({ query }: { query: string }) {
  const tokens = useMemo(() => tokenize(query), [query]);

  const styles: Record<QToken["t"], string> = {
    paren: "text-ink-400 font-mono px-1",
    phrase: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
    op: "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-bold",
    neg: "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
    tag: "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
    user: "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300",
    field: "bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-mono",
    term: "bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-200",
  };

  return (
    <div className="rounded-lg ring-1 ring-ink-100 dark:ring-ink-800 bg-ink-50/60 dark:bg-ink-950/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-2 flex items-center gap-1">
        <GitBranch size={11} /> Tokens interpretados
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tokens.map((t, i) => (
          <span key={i} className={cn("text-xs px-2 py-0.5 rounded", styles[t.t])}>
            {t.v}
          </span>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-ink-500">
        <Legend dotClass="bg-violet-500" label="Operador" />
        <Legend dotClass="bg-emerald-500" label="Frase exacta" />
        <Legend dotClass="bg-orange-500" label="Hashtag" />
        <Legend dotClass="bg-sky-500" label="@usuario" />
        <Legend dotClass="bg-brand-500" label="campo:valor" />
        <Legend dotClass="bg-red-500" label="Exclusión" />
        <Legend dotClass="bg-ink-400" label="Término" />
      </div>
    </div>
  );
}

function Legend({ dotClass, label }: { dotClass: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={cn("w-2 h-2 rounded-full", dotClass)} /> {label}
    </span>
  );
}

// ----- Filters -----

function FiltersSection() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <FilterGroup title="Geografía e idioma" icon={Globe}>
        <FilterField label="Idiomas a recolectar" defaultValue={["ES", "EN"]} options={["ES", "PT", "EN", "FR"]} />
        <FilterField
          label="Países / regiones"
          defaultValue={["PE", "CO", "MX"]}
          options={["PE", "CO", "MX", "CL", "AR", "BR", "ES", "US"]}
        />
      </FilterGroup>

      <FilterGroup title="Tiempo y frecuencia" icon={Clock}>
        <SelectField
          label="Ventana histórica"
          defaultValue="7 días"
          options={["Últimas 24 horas", "Últimos 7 días", "Últimos 30 días"]}
        />
        <SelectField
          label="Frecuencia de polling"
          defaultValue="Cada 5 min"
          options={["En tiempo real", "Cada 1 min", "Cada 5 min", "Cada 15 min", "Cada hora"]}
        />
      </FilterGroup>

      <FilterGroup title="Calidad y volumen" icon={FilterIcon}>
        <SelectField
          label="Alcance mínimo"
          defaultValue="Sin mínimo"
          options={["Sin mínimo", "> 100 seguidores", "> 1.000 seguidores", "> 10.000 seguidores"]}
        />
        <SelectField label="Filtro anti-spam" defaultValue="Estricto" options={["Desactivado", "Suave", "Estricto"]} />
      </FilterGroup>

      <FilterGroup title="Exclusiones" icon={XOctagon}>
        <ExclusionField label="Cuentas excluidas" defaults={["@spam_bot"]} placeholder="@cuenta o palabra…" />
        <ExclusionField label="Palabras prohibidas" defaults={["empleo", "vacante"]} placeholder="palabra…" />
      </FilterGroup>
    </div>
  );
}

function FilterGroup({ title, icon: IconCmp, children }: { title: string; icon: typeof Globe; children: React.ReactNode }) {
  return (
    <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4 space-y-3">
      <h4 className="text-sm font-semibold text-ink-900 dark:text-white flex items-center gap-1.5">
        <IconCmp size={14} className="text-ink-500" /> {title}
      </h4>
      {children}
    </div>
  );
}

function FilterField({
  label,
  defaultValue,
  options,
}: {
  label: string;
  defaultValue: string[];
  options: string[];
}) {
  const [vals, setVals] = useState<string[]>(defaultValue);
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-700 dark:text-ink-300">{label}</span>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {options.map((o) => {
          const on = vals.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => setVals((v) => (v.includes(o) ? v.filter((x) => x !== o) : [...v, o]))}
              className={cn(
                "text-xs px-2 py-1 rounded-md font-mono transition-colors",
                on
                  ? "bg-brand-600 text-white"
                  : "bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700",
              )}
              aria-pressed={on}
            >
              {o}
            </button>
          );
        })}
      </div>
    </label>
  );
}

function SelectField({
  label,
  defaultValue,
  options,
}: {
  label: string;
  defaultValue: string;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-700 dark:text-ink-300">{label}</span>
      <select
        defaultValue={defaultValue}
        className="mt-1 w-full h-9 px-2.5 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 bg-white dark:bg-ink-900 text-sm outline-none focus:ring-2 focus:ring-brand-500"
      >
        {options.map((o) => (
          <option key={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

function ExclusionField({
  label,
  defaults,
  placeholder,
}: {
  label: string;
  defaults: string[];
  placeholder: string;
}) {
  const [items, setItems] = useState<string[]>(defaults);
  const [draft, setDraft] = useState("");
  function add() {
    const v = draft.trim();
    if (v && !items.includes(v)) setItems([...items, v]);
    setDraft("");
  }
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-700 dark:text-ink-300">{label}</span>
      <div className="mt-1 flex flex-wrap gap-1.5 p-2 rounded-lg ring-1 ring-ink-200 dark:ring-ink-700 bg-white dark:bg-ink-900 min-h-[44px]">
        {items.map((t) => (
          <span
            key={t}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs"
          >
            {t}
            <button
              type="button"
              onClick={() => setItems(items.filter((x) => x !== t))}
              className="text-red-400 hover:text-bad"
              aria-label={`Quitar ${t}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
          placeholder={placeholder}
          className="flex-1 min-w-[100px] bg-transparent text-xs outline-none px-1"
        />
      </div>
    </label>
  );
}

// ----- Options -----

function OptionsSection({ meta }: { meta: NetworkMeta }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4">
        <h4 className="text-sm font-semibold text-ink-900 dark:text-white flex items-center gap-1.5 mb-3">
          <Settings2 size={14} className="text-ink-500" /> Específicas de {meta.label}
        </h4>
        <div className="space-y-1">
          {meta.extras.map((e) => (
            <ToggleRow key={e.id} label={e.label} defaultOn={e.def} />
          ))}
        </div>
      </div>

      <div className="rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4">
        <h4 className="text-sm font-semibold text-ink-900 dark:text-white flex items-center gap-1.5 mb-3">
          <Bell size={14} className="text-ink-500" /> Alertas para esta red
        </h4>
        <div className="space-y-1">
          <ToggleRow label="Crisis · pico de menciones > 200%" defaultOn />
          <ToggleRow label="Mención de influencer · 100k+ seguidores" defaultOn />
          <ToggleRow label="Sentimiento negativo · > 10 menciones / h" defaultOn={false} />
          <ToggleRow label="Cuenta verificada nos menciona" defaultOn={false} />
        </div>
      </div>

      <div className="md:col-span-2 rounded-xl ring-1 ring-ink-100 dark:ring-ink-800 p-4">
        <h4 className="text-sm font-semibold text-ink-900 dark:text-white flex items-center gap-1.5 mb-3">
          <Database size={14} className="text-ink-500" /> Recolección de datos
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ToggleCard label="Texto del post" desc="Contenido completo" defaultOn />
          <ToggleCard label="Imágenes y video" desc="Adjuntos multimedia" defaultOn />
          <ToggleCard label="Métricas públicas" desc="Likes, shares, comentarios" defaultOn />
          <ToggleCard label="Perfil del autor" desc="Seguidores, bio, ubicación" defaultOn />
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, defaultOn }: { label: string; defaultOn: boolean }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <div className="flex items-center justify-between p-2.5 rounded-md hover:bg-ink-50 dark:hover:bg-ink-800">
      <span className="text-sm text-ink-700 dark:text-ink-200">{label}</span>
      <button
        type="button"
        onClick={() => setOn(!on)}
        className={cn(
          "h-5 w-9 rounded-full transition-colors relative",
          on ? "bg-brand-600" : "bg-ink-300 dark:bg-ink-700",
        )}
        aria-pressed={on}
        aria-label={label}
      >
        <span
          className={cn(
            "absolute top-[3px] h-3.5 w-3.5 rounded-full bg-white shadow transition-all",
            on ? "left-[19px]" : "left-[3px]",
          )}
        />
      </button>
    </div>
  );
}

function ToggleCard({
  label,
  desc,
  defaultOn,
}: {
  label: string;
  desc: string;
  defaultOn: boolean;
}) {
  const [on, setOn] = useState(defaultOn);
  return (
    <button
      type="button"
      onClick={() => setOn(!on)}
      className={cn(
        "text-left p-3 rounded-lg ring-1 transition-all",
        on
          ? "ring-brand-300 bg-brand-50/50 dark:bg-brand-900/20 dark:ring-brand-700"
          : "ring-ink-200 dark:ring-ink-700 hover:bg-ink-50 dark:hover:bg-ink-800",
      )}
      aria-pressed={on}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink-900 dark:text-white">{label}</p>
          <p className="text-[11px] text-ink-500 mt-0.5">{desc}</p>
        </div>
        <div
          className={cn(
            "w-5 h-5 rounded-md flex items-center justify-center shrink-0",
            on ? "bg-brand-600 text-white" : "ring-1 ring-ink-300 dark:ring-ink-700",
          )}
        >
          {on && <Check size={12} />}
        </div>
      </div>
    </button>
  );
}
